import React from 'react'

// Components
import { EditableDocumentDescription } from '../EditableDocumentDescription'
import { EditableDocumentName } from '../EditableDocumentName'
import { IsSignedIn } from 'containers/IsSignedIn'
import { PersonAvatar, Tooltip } from '@sketch/components'

import { PresenceUsers } from 'modules/presence/components'

// Internal Components
import DocumentNotificationButton from '../DocumentNotificationButton'

import {
  useDocumentDescriptionChange,
  useDocumentNameChange,
} from './AboutTab.hooks'

import {
  Button,
  ActionWrapper,
  HeaderSection,
  Copy,
  AuthorSection,
  AuthorWrapper,
} from './AboutTab.styles'

import { ShareAboutFragment } from '@sketch/gql-types'
import { ShareType } from 'modules/shares/components/EditInSketchButton'

const DOCUMENT_TYPE_COPY = {
  STANDARD: 'Document',
  LIBRARY: 'Library',
  TEMPLATE: 'Template',
}

const getDocumentType = (type: ShareType) => {
  if (type in DOCUMENT_TYPE_COPY) {
    return DOCUMENT_TYPE_COPY[type]
  }

  return DOCUMENT_TYPE_COPY['STANDARD']
}

interface AboutTabHeaderSectionProps {
  share: ShareAboutFragment
  showSettingsModal: () => void
}
const AboutTabHeaderSection = ({
  share,
  showSettingsModal,
}: AboutTabHeaderSectionProps) => {
  const { creator } = share.version || {}
  const changeDocumentDescription = useDocumentDescriptionChange()
  const changeDocumentName = useDocumentNameChange()

  const descriptionEditHandler = (description: string) => {
    changeDocumentDescription({
      variables: { identifier: share.identifier, description },
    })
  }

  const nameEditHandler = (name: string) => {
    changeDocumentName({ variables: { identifier: share.identifier, name } })
  }
  return (
    <HeaderSection data-testid="about-tab-header">
      <Copy data-testid="document-type">{getDocumentType(share.type)}</Copy>
      <EditableDocumentName
        name={share.name}
        editHandler={share.userCanUpdateSettings ? nameEditHandler : undefined}
      />
      <EditableDocumentDescription
        description={share.description || ''}
        editHandler={
          share.userCanUpdateSettings ? descriptionEditHandler : undefined
        }
      />

      <PresenceUsers
        shareIdentifier={share.identifier}
        forceFallback={!share?.workspace.userIsMember}
        fallback={() => {
          const creatorName = share.version?.userIsCreator
            ? 'You'
            : share.version?.creator?.name!
          const avatar = (
            <PersonAvatar
              flavour="image"
              size="16px"
              src={creator?.avatar?.small!}
              name={creatorName}
            />
          )

          return (
            <AuthorSection>
              <Copy>Last save from</Copy>{' '}
              <Tooltip
                content={
                  <AuthorWrapper>
                    {avatar} {creatorName}
                  </AuthorWrapper>
                }
                placement="bottom-start"
                spacing="0px"
                scrollable
              >
                {avatar}
              </Tooltip>
            </AuthorSection>
          )
        }}
      />

      <IsSignedIn>
        {(share.userCanUpdateSettings || share.commentsEnabled) && (
          <ActionWrapper>
            {share.userCanUpdateSettings && (
              <Button variant="secondary" size="32" onClick={showSettingsModal}>
                Share…
              </Button>
            )}
            {share.commentsEnabled && (
              <DocumentNotificationButton
                identifier={share.identifier}
                currentStatus={share.subscriptionStatus}
              />
            )}
          </ActionWrapper>
        )}
      </IsSignedIn>
    </HeaderSection>
  )
}

export default AboutTabHeaderSection
